<script setup lang="ts">
import { computed } from 'vue'
import { Loader2 } from 'lucide-vue-next'

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline' | 'success'
export type ButtonSize = 'sm' | 'md' | 'lg'

interface Props {
  variant?: ButtonVariant
  size?: ButtonSize
  type?: 'button' | 'submit' | 'reset'
  disabled?: boolean
  loading?: boolean
  block?: boolean
  iconOnly?: boolean
  ariaLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'md',
  type: 'button',
  disabled: false,
  loading: false,
  block: false,
  iconOnly: false,
})

const variantClass = computed(() => {
  switch (props.variant) {
    case 'primary':
      return 'v2-btn-primary text-white'
    case 'secondary':
      return 'bg-bg-elevated text-fg hover:bg-bg-hover border border-border'
    case 'danger':
      return 'bg-danger text-white hover:opacity-90 active:opacity-80 shadow-sm'
    case 'success':
      return 'bg-success text-white hover:opacity-90 active:opacity-80 shadow-sm'
    case 'ghost':
      return 'bg-transparent text-fg hover:bg-bg-hover'
    case 'outline':
      return 'bg-transparent text-fg border border-border hover:bg-bg-hover'
    default:
      return ''
  }
})

const sizeClass = computed(() => {
  const map: Record<ButtonSize, string> = {
    sm: props.iconOnly ? 'h-8 w-8 text-xs' : 'h-8 px-3 text-xs',
    md: props.iconOnly ? 'h-9 w-9 text-sm' : 'h-9 px-3.5 text-sm',
    lg: props.iconOnly ? 'h-11 w-11 text-base' : 'h-11 px-5 text-base',
  }
  return map[props.size]
})

const isDisabled = computed(() => props.disabled || props.loading)
</script>

<template>
  <button
    :type="type"
    :disabled="isDisabled"
    :class="[
      'btn-base',
      variantClass,
      sizeClass,
      block ? 'w-full' : '',
      iconOnly ? 'p-0' : '',
    ]"
    :aria-label="ariaLabel"
    :aria-busy="loading || undefined"
  >
    <Loader2 v-if="loading" :size="size === 'lg' ? 18 : size === 'sm' ? 12 : 14" class="animate-spin" />
    <slot v-else name="icon" />
    <slot />
  </button>
</template>
